import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Download, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const today = new Date();
  const [scheduleYear, setScheduleYear] = useState(today.getFullYear());
  const [scheduleMonth, setScheduleMonth] = useState(today.getMonth() + 1);
  const [scheduleSupplier, setScheduleSupplier] = useState("");
  const [grnStart, setGrnStart] = useState("");
  const [grnEnd, setGrnEnd] = useState("");
  const [movMaterial, setMovMaterial] = useState("");
  const [movStart, setMovStart] = useState("");
  const [movEnd, setMovEnd] = useState("");
  const [materials, setMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    api.get("/materials").then((r) => setMaterials(r.data || []));
    api.get("/suppliers").then((r) => setSuppliers(r.data || []));
  }, []);

  const dl = async (path, fname) => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = fname;
      a.click();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed"); }
  };

  const Section = ({ title, children, testId }) => (
    <div className="bg-white border border-[#EEEEEE] rounded-sm p-5" data-testid={testId}>
      <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E] mb-3 border-l-4 border-[#1565C0] pl-2">{title}</div>
      {children}
    </div>
  );

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports Centre" subtitle="Downloadable Excel + PDF reports across modules"
        helpText="Same column structure as the Abnormal Report. Use filters per section then click Excel or PDF to download."
      />
      <div className="p-8 grid grid-cols-2 gap-6">
        <Section title="Schedule Adherence Report" testId="report-schedule">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">Year</label>
              <input type="number" value={scheduleYear} onChange={(e) => setScheduleYear(e.target.value)} className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm" data-testid="sched-year"/>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">Month</label>
              <select value={scheduleMonth} onChange={(e) => setScheduleMonth(+e.target.value)} className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm" data-testid="sched-month">
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">Supplier</label>
              <select value={scheduleSupplier} onChange={(e) => setScheduleSupplier(e.target.value)} className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm" data-testid="sched-supplier">
                <option value="">All Suppliers</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.code ? `${s.code} — ` : ""}{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => dl(`/reports/schedule.xlsx?period=monthly&year=${scheduleYear}&month=${scheduleMonth}${scheduleSupplier ? `&supplier_id=${scheduleSupplier}` : ""}`, `schedule_${scheduleYear}_${scheduleMonth}${scheduleSupplier ? "_" + scheduleSupplier.slice(0, 6) : ""}.xlsx`)} className="bg-[#2E7D32] text-white text-xs px-3 py-2 rounded-sm uppercase tracking-widest font-bold flex items-center gap-1" data-testid="sched-xlsx"><FileSpreadsheet size={12}/> Excel</button>
            <button onClick={() => dl(`/reports/schedule.pdf?period=monthly&year=${scheduleYear}&month=${scheduleMonth}${scheduleSupplier ? `&supplier_id=${scheduleSupplier}` : ""}`, `schedule_${scheduleYear}_${scheduleMonth}${scheduleSupplier ? "_" + scheduleSupplier.slice(0, 6) : ""}.pdf`)} className="bg-[#C62828] text-white text-xs px-3 py-2 rounded-sm uppercase tracking-widest font-bold flex items-center gap-1" data-testid="sched-pdf"><FileText size={12}/> PDF</button>
          </div>
        </Section>

        <Section title="GRN Report" testId="report-grn">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">From</label>
              <input type="date" value={grnStart} onChange={(e) => setGrnStart(e.target.value)} className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm" data-testid="grn-rep-start"/>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">To</label>
              <input type="date" value={grnEnd} onChange={(e) => setGrnEnd(e.target.value)} className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm" data-testid="grn-rep-end"/>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => dl(`/reports/grn.xlsx?start_date=${grnStart}&end_date=${grnEnd}`, "grn_report.xlsx")} className="bg-[#2E7D32] text-white text-xs px-3 py-2 rounded-sm uppercase tracking-widest font-bold flex items-center gap-1" data-testid="grn-rep-xlsx"><FileSpreadsheet size={12}/> Excel</button>
            <button onClick={() => dl(`/reports/grn.pdf?start_date=${grnStart}&end_date=${grnEnd}`, "grn_report.pdf")} className="bg-[#C62828] text-white text-xs px-3 py-2 rounded-sm uppercase tracking-widest font-bold flex items-center gap-1" data-testid="grn-rep-pdf"><FileText size={12}/> PDF</button>
          </div>
        </Section>

        <Section title="Inward + Outward Movement (Running Balance)" testId="report-movement">
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="col-span-3">
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">Material (optional)</label>
              <select value={movMaterial} onChange={(e) => setMovMaterial(e.target.value)} className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm" data-testid="mov-mat">
                <option value="">All materials</option>
                {materials.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">From</label>
              <input type="date" value={movStart} onChange={(e) => setMovStart(e.target.value)} className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm" data-testid="mov-start"/>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">To</label>
              <input type="date" value={movEnd} onChange={(e) => setMovEnd(e.target.value)} className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm" data-testid="mov-end"/>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => dl(`/reports/movement.xlsx?material_id=${movMaterial}&start_date=${movStart}&end_date=${movEnd}`, "movement.xlsx")} className="bg-[#2E7D32] text-white text-xs px-3 py-2 rounded-sm uppercase tracking-widest font-bold flex items-center gap-1" data-testid="mov-xlsx"><Download size={12}/> Download Excel</button>
          </div>
        </Section>
      </div>
    </div>
  );
}
